import { useEffect } from 'react';
import "./App.css";
import './styles/index.css';
import 'react-toastify/dist/ReactToastify.css';
import ReactDOM from 'react-dom';
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import { initLogger } from "Logger";
import { StylesProvider } from '@material-ui/core/styles';
import { Route, Router, Switch } from 'react-router-dom';
import { analyticsInitialize, handlePageChange } from 'analytics';
import { LicenseInfo } from '@mui/x-license-pro';
import { createBrowserHistory } from "history";
import InitializeApp from 'layouts/InitializeApp';
import { ToastContainer } from 'react-toastify';
import Hotjar from '@hotjar/browser';
import Unsubscribe from 'pages/Unsubscribe';
import PublicRoutes from 'layouts/PublicRoutes';
import LoaderOverlay from 'elements/LoaderOverlay';

/** Environment and configuration constants */
const HOTJAR_ID = Number(process.env.REACT_APP_HOTJAR_ID);
const HOTJAR_VERSION = Number(process.env.REACT_APP_HOTJAR_VERSION);
const IS_PROD = process.env.NODE_ENV === 'production';

// Initialize services
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE as string);
analyticsInitialize();
initLogger();

/** Browser history instance for routing */
const history = createBrowserHistory();

// Configure Sentry
Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
    maxBreadcrumbs: 10,
    environment: process.env.NODE_ENV,
});

// Track page changes
history.listen(() => {
    handlePageChange();
});

/** Router component that handles authentication-based routing */
const AppRouter = () => {
    const { isAuthenticated, isLoading } = useAuth0();
    if (isLoading) {
        return (
            <LoaderOverlay
                className="loaderFullScreenAuth"
                title=""
            >
                <div
                    className="authenticatingUser"
                    style={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: 20,
                        marginTop: 10,
                    }}
                >
                    Authenticating...
                </div>
            </LoaderOverlay>
        );
    }

    return (
        <Switch>
            <Route path="/unsubscribe" component={Unsubscribe} />
            {!isAuthenticated ? (
                // Public routes for unauthenticated users
                <Route path={['/', '/homepage', '/adbox/campaigns/:campaignId', '/chats/:chatId']}>
                    <PublicRoutes />
                </Route>
            ) : (
                // Authenticated routes
                <Route path="/">
                    <InitializeApp />
                    <ToastContainer />
                </Route>
            )}
        </Switch>
    );
};

/** Main application component with providers */
const MainApp = () => {
    useEffect(() => {
        if (IS_PROD) {
            Hotjar.init(HOTJAR_ID, HOTJAR_VERSION);
        }
    }, []);

    const onRedirectCallback = (appState?: any) => {
        // Handle custom redirect after login
        const targetUrl = appState?.returnTo || window.location.pathname;
        
        // Check if this was a signup flow
        const signupContext = localStorage.getItem('signup_context');
        if (signupContext) {
            try {
                const context = JSON.parse(signupContext);
                console.log('Post-login signup context:', context);
                
                // If user was in a signup flow, we might want to trigger plan selection
                // This could be handled by the parent components or with a custom event
                window.dispatchEvent(new CustomEvent('auth-signup-complete', { 
                    detail: context 
                }));
                
                // Clean up the context
                localStorage.removeItem('signup_context');
            } catch (error) {
                console.error('Error parsing signup context:', error);
            }
        } else {
            // Handle temporary brand migration for existing users returning from social login
            handleTemporaryBrandMigrationOnLogin();
        }
        
        // Navigate to the target URL using React Router's history
        history.push(targetUrl);
    };

    /** Handle temporary brand migration for existing users */
    const handleTemporaryBrandMigrationOnLogin = async () => {
        try {
            const anonymousSessionId = localStorage.getItem('anonymous-session-id');
            if (!anonymousSessionId) {
                return; // No temporary brand to migrate
            }

            // Wait a bit for user/brand initialization to complete
            setTimeout(async () => {
                try {
                    // Import the hook dynamically to avoid dependency issues
                    const { default: useBrand } = await import('Hooks/useBrand');
                    const { currentBrand, migrateTemporaryToRealBrand } = useBrand.getState();
                    
                    if (currentBrand && !currentBrand.slug?.startsWith('temp-')) {
                        console.log('🔄 Migrating temporary brand for existing user:', currentBrand.id);
                        await migrateTemporaryToRealBrand(currentBrand.id);
                        console.log('✅ Temporary brand migration completed for existing user');
                    }
                } catch (error) {
                    console.error('Failed to migrate temporary brand for existing user:', error);
                }
            }, 2000); // Wait 2 seconds for brand initialization
        } catch (error) {
            console.error('Error in temporary brand migration handler:', error);
        }
    };

    return (
        <Auth0Provider
            domain={process.env.REACT_APP_AUTH0_DOMAIN as string}
            clientId={process.env.REACT_APP_AUTH0_CLIENT_ID as string}
            audience={process.env.REACT_APP_AUTH0_AUDIENCE as string}
            scope="openid profile"
            redirectUri={window.location.origin}
            onRedirectCallback={onRedirectCallback}
            // useRefreshTokens={true}
            // cacheLocation="localstorage"
        >
            <StylesProvider injectFirst>
                <Router history={history}>
                    <AppRouter />
                </Router>
            </StylesProvider>
        </Auth0Provider>
    );
};

ReactDOM.render(
    <MainApp />,
    document.getElementById('root')
);